import { useState, useEffect } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import WhatsAppSendModal from "../components/WhatsAppSendModal";
import { toast } from "sonner";
import { WhatsappLogo, Wrench } from "@phosphor-icons/react";

const ACTION_LABELS = {
  install_schedule: "Schedule",
  technician_assigned: "Notify Technician",
  service_reminder: "Reminder",
  install_completed: "Completed",
  pending_issue: "Issue Update",
};

const ServiceTasks = ({ user, logout }) => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [wa, setWa] = useState(null);

  const load = async () => {
    try {
      const [tRes, uRes] = await Promise.all([
        api.get(`${API}/crm/tasks`),
        api.get(`/users`),
      ]);
      // Filter to service/installation tasks only
      const filtered = (tRes.data || []).filter(t =>
        ["service", "installation", "site_visit"].includes(t.type) || /install|service/i.test(t.title || "")
      );
      setTasks(filtered);
      setUsers(uRes.data || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const openWaCustomer = (t, action) => {
    setWa({
      module: "service",
      receiver: { name: t.contact_name || t.customer_name || "Customer", phone: t.contact_phone || t.customer_phone, type: "customer" },
      defaultAction: action,
      context: {
        variables: {
          name: t.contact_name || t.customer_name || "",
          customer_name: t.contact_name || "",
          doc_number: t.task_id?.slice(0, 8) || "",
          scheduled_at: (t.due_date || "").slice(0, 16).replace("T", " "),
          completed_at: (t.completed_at || "").slice(0, 16).replace("T", " "),
          service_location: t.location || t.address || "",
          technician_name: t.assigned_to || "",
          issue: t.description || "",
          eta: (t.due_date || "").slice(0, 16).replace("T", " "),
        },
      },
    });
  };

  const openWaTechnician = (t) => {
    const tech = users.find(u => u.user_id === t.assigned_to_id || u.name === t.assigned_to);
    if (!tech) { toast.error("Technician not assigned or not found in users"); return; }
    if (!tech.whatsapp_number && !tech.phone) { toast.error("Technician WhatsApp number not set. Add it in Admin → Users."); return; }
    setWa({
      module: "service",
      receiver: { name: tech.name, phone: tech.whatsapp_number || tech.phone, type: "staff" },
      defaultAction: "technician_assigned",
      context: {
        variables: {
          name: tech.name || "",
          customer_name: t.contact_name || "",
          doc_number: t.task_id?.slice(0, 8) || "",
          service_location: t.location || t.address || "",
          scheduled_at: (t.due_date || "").slice(0, 16).replace("T", " "),
        },
      },
    });
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Service Department</div>
          <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Service & Installation</h1><HelpButton moduleKey="service" /></div>
          <p className="text-base text-slate-700 mt-1">{tasks.length} active tickets · WhatsApp customers + technicians</p>
        </div>

        <div data-testid="service-table" className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              {["Title", "Customer", "Technician", "Schedule", "Status", "WhatsApp"].map(h => <th key={h} className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {tasks.length === 0 ? <tr><td colSpan={6} className="p-12 text-center text-slate-400 text-sm">No service tickets. Create from CRM Tasks with type='service' or 'installation'.</td></tr> :
              tasks.map((t, i) => (
                <tr key={t.task_id} data-testid={`service-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3 text-sm">
                    <div className="font-semibold">{t.title || "—"}</div>
                    <div className="text-xs text-slate-500 capitalize">{t.type || "task"}</div>
                  </td>
                  <td className="p-3 text-sm">{t.contact_name || t.customer_name || "—"}</td>
                  <td className="p-3 text-sm">{t.assigned_to || "—"}</td>
                  <td className="p-3 text-xs text-slate-500">{(t.due_date || "—").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3"><span className="text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">{t.status || "pending"}</span></td>
                  <td className="p-3 whitespace-nowrap">
                    <button data-testid={`svc-wa-cust-${i}`} onClick={() => openWaCustomer(t, "install_schedule")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1"><WhatsappLogo size={12} weight="fill" />Customer</button>
                    <button data-testid={`svc-wa-tech-${i}`} onClick={() => openWaTechnician(t)} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 ml-1 font-bold">Technician</button>
                    <button data-testid={`svc-wa-done-${i}`} onClick={() => openWaCustomer(t, "install_completed")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 ml-1 font-bold">Completed</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {wa && <WhatsAppSendModal open={true} onClose={() => setWa(null)} {...wa} user={user} />}
    </div>
  );
};

export default ServiceTasks;
